import React, { useState, useRef, useEffect } from 'react';
import apiService from '../services/api';

const UnifiedChatAssistant = ({
  project,
  files = [],
  currentFile,
  selectedCode = '',
  onCodeGenerated,
  onClose,
  variant = 'modal'
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeMode, setActiveMode] = useState('chat');
  const [showQuickActions, setShowQuickActions] = useState(false);
  const messagesEndRef = useRef(null);
  const projectId = project?.id;
  const projectName = project?.name || 'your project';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history from localStorage
  useEffect(() => {
    if (!projectId) return;
    const savedMessages = localStorage.getItem(`chat_${projectId}`);
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      // Welcome message
      setMessages([
        {
          role: 'assistant',
          content: `👋 Hey! I'm your standalone AI coding assistant for **${projectName}**.\n\n**I can help you:**\n✨ Generate new code and features\n🔧 Fix bugs and issues\n♻️ Refactor and optimize code\n📚 Explain complex code\n✅ Review code quality\n📝 Write documentation\n\n**Quick tips:**\n• Use Quick Actions below for common tasks\n• Select code in the editor for context-aware help\n• All conversations are saved automatically\n\nWhat would you like to work on?`
        }
      ]);
    }
  }, [projectId, projectName]);

  // Save chat history to localStorage
  useEffect(() => {
    if (messages.length > 0 && projectId) {
      localStorage.setItem(`chat_${projectId}`, JSON.stringify(messages));
    }
  }, [messages, projectId]);

  const getContext = () => {
    if (!project) {
      return 'No project selected.';
    }

    let context = `Project: ${project.name}\nLanguage: ${project.language}\n`;

    if (currentFile) {
      context += `\nCurrent File: ${currentFile.path}\n`;
      if (selectedCode) {
        context += `\nSelected Code:\n\`\`\`${currentFile.language}\n${selectedCode}\n\`\`\`\n`;
      }
    }

    // Add file list for context
    const fileList = files
      .filter(f => f.type === 'file')
      .slice(0, 30)
      .map(f => f.path)
      .join('\n');

    context += `\nProject Files (first 30):\n${fileList}`;

    return context;
  };

  const handleSubmit = async (e, customPrompt = null) => {
    e?.preventDefault();
    const userMessage = customPrompt || input.trim();
    if (!userMessage || loading) return;

    setInput('');
    setShowQuickActions(false);

    // Add user message
    const newUserMessage = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const taskType = activeMode === 'chat' ? 'code_generation' : `code_${activeMode}`;

      const aiResponse = await apiService.generateWithAI({
        task_type: taskType,
        prompt: userMessage,
        language: currentFile?.language || project.language || 'javascript',
        context: getContext(),
        project_id: project.id,
        file_id: currentFile?.id
      });

      // Add AI response
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: aiResponse.result,
          confidence: aiResponse.confidence
        }
      ]);

    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `❌ Error: ${error.message}\n\nPlease try again or check your OpenAI API key configuration.`
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      label: '✨ Add Feature',
      prompt: 'Help me add a new feature to this project',
      mode: 'chat'
    },
    {
      label: '🐛 Fix Bug',
      prompt: selectedCode ? `Fix any bugs in this code: ${selectedCode}` : 'Help me debug an issue',
      mode: 'fix'
    },
    {
      label: '♻️ Refactor',
      prompt: selectedCode ? `Refactor this code for better performance and readability: ${selectedCode}` : 'Suggest refactoring improvements',
      mode: 'refactor'
    },
    {
      label: '📚 Explain',
      prompt: selectedCode ? `Explain this code in detail: ${selectedCode}` : 'Explain how this project works',
      mode: 'explain'
    },
    {
      label: '✅ Review',
      prompt: currentFile ? `Review the code in ${currentFile.path} for best practices` : 'Review my project code',
      mode: 'review'
    },
    {
      label: '📝 Document',
      prompt: selectedCode ? `Generate documentation for this code: ${selectedCode}` : 'Generate project documentation',
      mode: 'documentation'
    }
  ];

  const clearChat = () => {
    if (projectId && window.confirm('Clear chat history? This cannot be undone.')) {
      setMessages([]);
      localStorage.removeItem(`chat_${projectId}`);
      // Reset with welcome message
      setMessages([
        {
          role: 'assistant',
          content: `Chat cleared! Ready to help you with **${projectName}**. What would you like to work on?`
        }
      ]);
    }
  };

  const copyCode = (code) => {
    // Extract code from markdown blocks
    const codeMatch = code.match(/```[\w]*\n([\s\S]*?)\n```/);
    const textToCopy = codeMatch ? codeMatch[1] : code;

    navigator.clipboard.writeText(textToCopy).then(() => {
      // Could show toast notification
    });
  };

  const insertCode = (code) => {
    const codeMatch = code.match(/```[\w]*\n([\s\S]*?)\n```/);
    const codeToInsert = codeMatch ? codeMatch[1] : code;

    if (onCodeGenerated) {
      onCodeGenerated(codeToInsert);
    }
  };

  const isModal = variant === 'modal';

  return (
    <div className={isModal ? 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4' : 'h-full w-full'}>
      <div className={isModal ? 'bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700' : 'bg-white dark:bg-gray-900 h-full flex flex-col border border-gray-200 dark:border-gray-700'}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364-.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white">
                AI Code Assistant
              </h2>
              <p className="text-xs md:text-sm text-white/80">
                {projectName} • {currentFile?.name || 'Project-wide'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={clearChat}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              title="Clear chat"
              disabled={!projectId}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Quick Actions</span>
            <svg className={`w-4 h-4 transition-transform ${showQuickActions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showQuickActions && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    setActiveMode(action.mode);
                    handleSubmit(e, action.prompt);
                  }}
                  className="px-3 py-2 text-xs md:text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600 dark:hover:text-purple-400 border border-gray-200 dark:border-gray-600 transition-all"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {!projectId && (
            <div className="flex items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
              <div>
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Select a project to start chatting</p>
                <p className="text-sm mt-2">Choose a project from the sidebar to unlock the assistant.</p>
              </div>
            </div>
          )}
          {projectId && messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] md:max-w-[75%] rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="text-sm md:text-base whitespace-pre-wrap break-words markdown-content">
                  {msg.content}
                </div>

                {msg.role === 'assistant' && msg.content.includes('```') && (
                  <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => copyCode(msg.content)}
                      className="text-xs px-3 py-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      📋 Copy Code
                    </button>
                    {onCodeGenerated && (
                      <button
                        onClick={() => insertCode(msg.content)}
                        className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                      >
                        ✅ Insert
                      </button>
                    )}
                  </div>
                )}

                {msg.confidence && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Confidence: {Math.round(msg.confidence * 100)}%
                  </div>
                )}
              </div>
            </div>
          ))}

          {projectId && loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="spinner"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    AI is thinking...
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Context Info */}
        {projectId && (selectedCode || currentFile) && (
          <div className="px-4 md:px-6 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-2 text-xs text-blue-800 dark:text-blue-300">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                {selectedCode && <p>✨ Code selected - AI will use it as context</p>}
                {currentFile && !selectedCode && <p>📄 Working with {currentFile.name}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <form onSubmit={handleSubmit} className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about your code..."
              className="flex-1 px-4 py-3 md:py-4 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-400"
              disabled={loading || !projectId}
              autoFocus
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || !projectId}
              className="px-6 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium"
            >
              <span>Send</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </form>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            💾 Your conversation is automatically saved • Powered by OpenAI GPT-4
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedChatAssistant;
